import {
  entries,
  filter,
  forEach,
  isNonNullish,
  map,
  pipe,
  tap,
  unique
} from 'remeda'
import {
  ICONS_BASE_URL,
  type IconBaseUrl,
  iconUrl
} from '../../../helpers/icon'
import type { IconWeight } from '../../../helpers/icon-source'
import type { Color } from '../../../types/colors'
import { WRAPPER_CLASS_LIST } from '../constants'
import type { ValidationPredicate } from './validators'
import { createValidator, isValidString } from './validators'

/**
 * Beautiful functional DOM utilities
 * Type-safe DOM manipulation using functional composition patterns
 */

// DOM element types and configurations
type HTMLElementSetup<T extends HTMLElement = HTMLElement> = (
  element: T
) => void

type ElementAttributes = Record<string, string | number | boolean | undefined>

type BadgeData = {
  readonly label?: string
  readonly color?: string
  readonly icon?: string
}

type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * CSS Class Validation - pure functional validation
 */
const isValidCssClass: ValidationPredicate<string> = createValidator(
  isValidString,
  (className: string): className is string => /^[a-zA-Z0-9_-]+$/.test(className)
)

/**
 * DOM Creation Utilities - functional composition with strong typing
 */

/** Create wrapper div with validated CSS classes and functional composition */
export const createWrapper = (
  additionalClasses: readonly string[] = []
): HTMLDivElement => {
  const validClasses = additionalClasses.filter(isValidCssClass)

  return pipe(
    document.createElement('div'),
    tap((el: HTMLDivElement) => {
      el.classList.add(...WRAPPER_CLASS_LIST, ...validClasses)
    })
  )
}

/** Set attributes with type safety and null filtering */
export const setAttributes = (
  element: HTMLElement,
  attrs: ElementAttributes
): void => {
  const attrEntries = entries(attrs)
  const validEntries = filter(
    attrEntries,
    (entry): entry is [string, string | number | boolean] =>
      isNonNullish(entry[1])
  )
  forEach(validEntries, ([key, value]) => {
    element.setAttribute(key, String(value))
  })
}

const elementTitle = (element: HTMLElement): string =>
  element.getAttribute('title')?.trim() ||
  element.getAttribute('aria-label')?.trim() ||
  ''

const descendantTitles = (element: HTMLElement): string =>
  pipe(
    [...element.querySelectorAll<HTMLElement>('[title], [aria-label]')],
    map(elementTitle),
    filter((title) => title.length > 0),
    unique()
  ).join(', ')

const primitiveTitle = (value: unknown): string =>
  value === null || value === undefined || typeof value === 'object'
    ? ''
    : String(value)

const isHidden = (element: HTMLElement): boolean =>
  element.hasAttribute('hidden') || element.style.display === 'none'

const renderedTitle = (
  element: HTMLElement,
  value: unknown,
  includeOwnTitle: boolean
): string => {
  if (isHidden(element)) {
    return primitiveTitle(value)
  }

  const ownTitle = includeOwnTitle
    ? elementTitle(element)
    : element.getAttribute('aria-label')?.trim()
  const text = element.textContent?.trim()

  return includeOwnTitle
    ? ownTitle || descendantTitles(element) || text || primitiveTitle(value)
    : ownTitle || text || descendantTitles(element) || primitiveTitle(value)
}

/** Keep the renderer root's native browser tooltip aligned with its content. */
export const syncCellTitle = (
  root: HTMLElement,
  content: HTMLElement,
  value: unknown
): void => {
  root.setAttribute('title', renderedTitle(content, value, root !== content))
}

/** Generic element creator with type-safe setup function */
export function createElement<T extends HTMLElement = HTMLElement>(
  tag: string,
  setup: HTMLElementSetup<T>
): T {
  const element = document.createElement(tag) as T
  setup(element)
  return element
}

/**
 * Specialized Element Creators - domain-specific factories
 */

export { createIcon } from '../../../helpers/icon'

/** Escape HTML special characters — defense-in-depth (backend already sanitizes) */
export const escapeHTML = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/** CSS custom properties an icon `<i>` element carries (size + url) */
type IconVars = {
  readonly '--icon-size': string
  readonly '--icon-url': string
}

/**
 * The `--icon-size`/`--icon-url` CSS-var contract shared by `iconHTML` (string)
 * and the template renderers (which set the same vars on a live `<i>` node).
 */
export const iconVars = (
  name: string,
  size: 'xs' | 'sm' = 'sm',
  baseUrl: IconBaseUrl = ICONS_BASE_URL
): IconVars => ({
  '--icon-size': `var(--icon-size-${size})`,
  '--icon-url': `url('${iconUrl(name, 'regular', baseUrl)}')`
})

/** Icon HTML matching data-component=".icon" markup */
export const iconHTML = (
  name: string,
  size: 'xs' | 'sm' = 'sm',
  baseUrl: IconBaseUrl = ICONS_BASE_URL
): string => {
  if (!name) {
    return ''
  }

  const vars = iconVars(name, size, baseUrl)

  return `<span data-component=".icon"><i style="${escapeHTML(`--icon-size:${vars['--icon-size']};--icon-url:${vars['--icon-url']}`)}"></i></span>`
}

/**
 * Plain-DOM icon element (`<span data-component=".icon"><i></i></span>`) — the
 * single source of truth for grid cell icons. Replaces the per-cell
 * `<facade-icon>` custom element (no upgrade cost). Shares the `iconVars`
 * contract with `iconHTML`, so string and element paths stay in lockstep.
 */
export const iconElement = (
  name: string,
  size: 'xs' | 'sm' = 'sm',
  baseUrl: IconBaseUrl = ICONS_BASE_URL
): HTMLElement => {
  const span = document.createElement('span')
  span.dataset.component = '.icon'

  const i = document.createElement('i')
  const vars = iconVars(name, size, baseUrl)

  i.style.setProperty('--icon-size', vars['--icon-size'])
  i.style.setProperty('--icon-url', vars['--icon-url'])
  span.appendChild(i)

  return span
}

/** Resolved Radix CSS variables for a badge's background/border/text */
type BadgeColors = {
  readonly bg: string
  readonly border: string
  readonly text: string
}

/**
 * Map a color token to the global Radix color-scale name. `neutral` is an alias
 * for `gray`; every other token maps to itself. Shared by the badge and button
 * color resolvers so the `neutral`→`gray` rule lives in exactly one place.
 */
const radixColorName = (color: Color): string =>
  color === 'neutral' ? 'gray' : color

/**
 * Resolve a badge color to global Radix CSS variables.
 *
 * The `<facade-badge>` `--bg-/--border-/--text-` tokens are shadow-scoped and
 * do NOT exist in light DOM, so grid badges resolve the underlying global
 * `--color-<name>-<scale>` tokens directly (mirrors `progressHTML`). Scales
 * mirror the per-color token aliases in `facade/css/_badge.css`: bg=3,
 * border=5, text=11 for standard colors; `neutral`→gray; `brand` uses 1/2/7.
 */
export const resolveBadgeColors = (color: Color): BadgeColors => {
  if (color === 'brand') {
    return {
      bg: 'var(--color-brand-1)',
      border: 'var(--color-brand-2)',
      text: 'var(--color-brand-7)'
    }
  }

  const name = radixColorName(color)

  return {
    bg: `var(--color-${name}-3)`,
    border: `var(--color-${name}-5)`,
    text: `var(--color-${name}-11)`
  }
}

/**
 * CSS-var overrides for a colored grid button (the `data-color` path). These
 * mirror the `<facade-button>` `getColorStyles` output exactly (button.ts):
 * primary uses scales 9/10/11 + text 1; secondary uses 3/4/5 + text 11. The
 * static `.grid-btn--colored` rules in `_grid.css` consume these vars, so the
 * per-instance color never leaks into the stylesheet — same pattern as badge.
 */
export type ButtonColorVars = Readonly<Record<string, string>>

export const resolveButtonColors = (color: Color): ButtonColorVars => {
  const name = radixColorName(color)

  return {
    '--bg-color-primary': `var(--color-${name}-9)`,
    '--bg-color-primary-hover': `var(--color-${name}-10)`,
    '--border-color-primary': `var(--color-${name}-10)`,
    '--border-color-primary-hover': `var(--color-${name}-11)`,
    '--text-color-primary': `var(--color-${name}-1)`,
    '--bg-color-secondary': `var(--color-${name}-3)`,
    '--bg-color-secondary-hover': `var(--color-${name}-4)`,
    '--border-color-secondary': `var(--color-${name}-4)`,
    '--border-color-secondary-hover': `var(--color-${name}-5)`,
    '--text-color-secondary': `var(--color-${name}-11)`
  }
}

/** Badge HTML — plain light-DOM markup (no `<facade-badge>` custom element) */
export const badgeHTML = (data: BadgeData): string => {
  const label = escapeHTML(data?.label ?? '')
  const colors = resolveBadgeColors((data?.color as Color) ?? 'neutral')
  const iconPrefix = data?.icon ? iconHTML(data.icon, 'xs') : ''

  const style = `background-color:${colors.bg};border-color:${colors.border};color:${colors.text}`

  return `<span class="grid-badge" style="${style}" title="${label}">${iconPrefix}<span class="grid-badge__label">${label}</span></span>`
}

// Export types for external use
export type {
  BadgeColors,
  BadgeData,
  BadgeSize,
  ElementAttributes,
  HTMLElementSetup,
  IconVars,
  IconWeight
}
